import { DATE_FORMAT } from '@constants';
import { Chip, Typography } from '@gurupay/gp-ui-library';
import type { StatementProps } from '@hooks/useAllRequestedStatements';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { NonBreakingDate } from '../../messages/NonBreakingDate';
import { isActive, STATUS_COLOR, STATUS_LABEL } from '../utils';
import DownloadCell from './DownloadCell';

type StatementsMobileItemProps = Pick<
	StatementProps,
	| 'status'
	| 'created_at'
	| 'type'
	| 'account_uuid'
	| 'account_id'
	| 'id'
	| 'account_iban'
	| 'account_name'
	| 'date_from'
	| 'date_to'
>;

const StatementsMobileItem = ({
	status,
	created_at,
	type,
	account_uuid,
	account_id,
	id,
	account_iban,
	account_name,
	date_from,
	date_to,
}: StatementsMobileItemProps) => {
	const { t } = useTranslation('', { keyPrefix: 'statements' });

	return (
		<div className="flex flex-col gap-space-16">
			<span>
				<span className="flex justify-between">
					<span className="flex flex-col mb-space-4">
						<Typography
							className={clsx('font-bold', !isActive(status) && 'text-neutral-400')}
							level="small-text"
						>
							{account_iban}
						</Typography>
						<Typography
							className={clsx(
								'font-medium',
								!isActive(status) ? 'text-neutral-400' : 'text-neutral-600',
							)}
							level="small-text"
						>
							{account_name}
						</Typography>
					</span>
					{status === 'generated' && (
						<DownloadCell accountId={account_uuid ?? account_id} statementId={id} />
					)}
				</span>
				<Chip
					className="capitalize"
					color={STATUS_COLOR[status]}
					label={t(STATUS_LABEL[status])}
					size="medium"
					variant="filled"
				/>
			</span>
			<span>
				<Typography className="text-neutral-600 font-medium" level="small-text">
					{t('period')}
				</Typography>
				<Typography
					className={clsx(
						'font-medium uppercase block',
						!isActive(status) && 'text-neutral-400',
					)}
					level="small-text"
				>
					{format(date_from, DATE_FORMAT)} – {format(date_to, DATE_FORMAT)}
				</Typography>
			</span>
			<span>
				<Typography className="text-neutral-600 font-medium" level="small-text">
					{t('fileFormat')}
				</Typography>
				<Typography
					className={clsx(
						'font-medium uppercase block',
						!isActive(status) && 'text-neutral-400',
					)}
					level="small-text"
				>
					{type}
				</Typography>
			</span>
			<span>
				<Typography className="text-neutral-600 block font-medium" level="small-text">
					{t('date')}
				</Typography>
				<Typography
					className={clsx('font-medium', !isActive(status) && 'text-neutral-400')}
					level="small-text"
				>
					<NonBreakingDate date={created_at} />
				</Typography>
			</span>
		</div>
	);
};

export default StatementsMobileItem;
